import { falExpand } from 'shared/@generated/icons/falExpand';
import useEmblaCarousel from 'embla-carousel-react';
import {
  forwardRef,
  type ForwardedRef,
  useEffect,
  useState,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Image } from 'shared/components/image';
import { Video } from 'shared/components/video';
import type { GalleryProps, GalleryRef } from './index';
import { MediaAsset, MediaAssetType } from 'shared/@generated/models';
import {
  GalleryFullscreenModal,
  GalleryFullscreenModalRef,
} from 'shared/components/gallery/gallery-fullscreen-modal';
import {
  getMediaAssetVideoPosterUrl,
  getMediaAssetVideoUrl,
  getMediaAssetVideoFallbackUrl,
} from 'shared/library/media-asset-urls';
import { GalleryArrowButton } from './gallery-arrow-button';
import { GalleryDots } from './gallery-dots';
import { getGallerySlides } from './gallery-slides';
import { Icon } from 'shared/components/icon';

function supportsCarouselObservers() {
  return (
    typeof IntersectionObserver === 'function' &&
    typeof ResizeObserver === 'function' &&
    typeof MutationObserver === 'function'
  );
}

function GalleryContent(props: GalleryProps, ref: ForwardedRef<GalleryRef>) {
  const {
    items,
    className,
    defaultAlt,
    aspect = 'video',
    variant = 'default',
    enableFullscreen,
    showCounter,
    showThumbnails,
    showDots,
    arrowSize,
    arrowsOnMobile,
    priority = true,
    onSelectedMediaChange,
    onMediaPress,
    adSlide,
    overlay,
  } = props;
  const { t } = useTranslation('components');
  const isCardVariant = variant === 'card';
  const canOpenFullscreen = enableFullscreen ?? !isCardVariant;
  const shouldShowCounter = showCounter ?? false;
  const shouldShowThumbnails = showThumbnails ?? !isCardVariant;
  const resolvedArrowSize = arrowSize ?? (isCardVariant ? 'compact' : 'default');
  const shouldShowArrowsOnMobile = arrowsOnMobile ?? false;
  const hasAdSlide = !!adSlide;
  const slides = useMemo(() => getGallerySlides(items, hasAdSlide), [hasAdSlide, items]);
  const shouldLoop = slides.length > 1;
  const [emblaRefMain, emblaApiMain] = useEmblaCarousel({ loop: shouldLoop });
  const [emblaRefThumbs, emblaApiThumbs] = useEmblaCarousel({
    loop: false,
    containScroll: 'keepSnaps',
    dragFree: true,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [settledIndex, setSettledIndex] = useState(0);
  const [canUseCarousel, setCanUseCarousel] = useState(true);
  const fullscreenModalRef = useRef<GalleryFullscreenModalRef>(null);
  const setMainViewport = useCallback(
    (node: HTMLDivElement | null) => {
      const supported = supportsCarouselObservers();
      if (node) setCanUseCarousel(supported);
      emblaRefMain(supported ? node : null);
    },
    [emblaRefMain],
  );
  const setThumbnailsViewport = useCallback(
    (node: HTMLDivElement | null) => emblaRefThumbs(supportsCarouselObservers() ? node : null),
    [emblaRefThumbs],
  );
  const mainAspectClassName =
    aspect === 'square'
      ? 'aspect-square'
      : isCardVariant
        ? 'aspect-4/3'
        : 'aspect-square md:aspect-4/3';
  const mainFrameClassName = isCardVariant ? 'rounded-t' : 'md:rounded-lg';
  const thumbAspectClassName = aspect === 'square' ? 'aspect-square' : 'aspect-4/3';
  const imageAspectRatio = aspect === 'square' ? 1 : 4 / 3;
  const shouldShowDots = (showDots ?? !shouldShowThumbnails) && slides.length > 1;
  const selectedSlide = slides[selectedIndex];

  useEffect(() => {
    if (!canUseCarousel && slides[0]?.type === 'media') {
      onSelectedMediaChange?.(slides[0].item, slides[0].mediaIndex);
    }
    if (!emblaApiMain) return;

    const onSelect = () => {
      const idx = emblaApiMain.selectedScrollSnap();
      const selectedSlide = slides[idx];

      setSelectedIndex(idx);
      emblaApiThumbs?.scrollTo(idx);

      if (selectedSlide?.type === 'media') {
        onSelectedMediaChange?.(selectedSlide.item, selectedSlide.mediaIndex);
      }
    };

    const onSettle = () => {
      setSettledIndex(emblaApiMain.selectedScrollSnap());
    };

    emblaApiMain.on('select', onSelect);
    emblaApiMain.on('settle', onSettle);
    onSelect();
    onSettle();

    return () => {
      emblaApiMain.off('select', onSelect);
      emblaApiMain.off('settle', onSettle);
    };
  }, [canUseCarousel, emblaApiMain, emblaApiThumbs, onSelectedMediaChange, slides]);

  const scrollTo = useCallback(
    (index: number) => {
      emblaApiMain?.scrollTo(index);
    },
    [emblaApiMain],
  );

  const scrollPrev = useCallback(() => {
    emblaApiMain?.scrollPrev();
  }, [emblaApiMain]);

  const scrollNext = useCallback(() => {
    emblaApiMain?.scrollNext();
  }, [emblaApiMain]);

  useEffect(() => {
    if (!canUseCarousel || isCardVariant || slides.length <= 1) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;

      if (
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.tagName === 'SELECT' ||
        target?.isContentEditable ||
        document.querySelector('[data-testid="gallery-fullscreen"]')
      ) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canUseCarousel, isCardVariant, scrollNext, scrollPrev, slides.length]);

  const openFullscreen = useCallback(
    (index: number) => {
      if (canOpenFullscreen) fullscreenModalRef.current?.open(index);
    },
    [canOpenFullscreen],
  );

  useImperativeHandle(ref, () => ({ openFullscreen }), [openFullscreen]);

  const handleMediaPress = useCallback(
    (item: MediaAsset, mediaIndex: number) => {
      if (canOpenFullscreen) {
        openFullscreen(mediaIndex);
        return;
      }

      onMediaPress?.(item, mediaIndex);
    },
    [canOpenFullscreen, onMediaPress, openFullscreen],
  );

  return (
    <>
      <div
        className={`${isCardVariant ? 'flex flex-col' : '-mx-3 -mt-3 flex flex-col md:mx-0 md:mt-0'} ${className ?? ''}`}
      >
        <div
          data-testid="gallery-main"
          data-selected-index={selectedIndex}
          data-settled-index={settledIndex}
          className={`bg-brand-cream group relative w-full overflow-hidden ${mainFrameClassName} ${mainAspectClassName}`}
          ref={setMainViewport}
        >
          <div className="flex size-full">
            {(canUseCarousel ? slides : slides.slice(0, 1)).map((slide, i) => {
              const linearDistance = Math.abs(selectedIndex - i);
              const distanceFromSelected = shouldLoop
                ? Math.min(linearDistance, slides.length - linearDistance)
                : linearDistance;

              if (slide.type === 'ad') {
                return (
                  <div
                    key={slide.key}
                    data-testid={`gallery-ad-slide-${i}`}
                    aria-hidden={selectedIndex !== i}
                    inert={selectedIndex !== i}
                    className="bg-brand-cream relative flex size-full flex-shrink-0 items-center justify-center px-3"
                  >
                    {distanceFromSelected <= 1 && (
                      <div
                        data-testid="gallery-ad-content"
                        className="flex size-full items-center justify-center"
                      >
                        {adSlide}
                      </div>
                    )}
                  </div>
                );
              }

              const item = slide.item;
              const slideContent = (
                <>
                  {item.type === MediaAssetType.Video ? (
                    <Video
                      src={getMediaAssetVideoUrl(item)}
                      fallbackSrc={getMediaAssetVideoFallbackUrl(item)}
                      poster={getMediaAssetVideoPosterUrl(item)}
                      priority={priority && i === 0}
                      containerClassName="size-full"
                      videoClassName="object-cover"
                      autoPlay={false}
                      controls={false}
                      loop={true}
                      muted={false}
                      sizeType={'large'}
                    />
                  ) : (
                    <Image
                      src={item}
                      alt={item.description ?? defaultAlt}
                      containerClassName={`size-full ${canOpenFullscreen ? 'cursor-zoom-in' : onMediaPress ? 'cursor-pointer' : ''}`}
                      imageClassName="object-cover"
                      onClick={
                        canOpenFullscreen
                          ? () => handleMediaPress(item, slide.mediaIndex)
                          : undefined
                      }
                      priority={priority && i === 0}
                      sizeType={isCardVariant ? 'card' : 'large'}
                      maxSourceWidth={isCardVariant ? undefined : 960}
                      aspectRatio={imageAspectRatio}
                    />
                  )}

                  {item.description && !isCardVariant && (
                    <div className="absolute bottom-2 left-2 right-2 flex">
                      <div className="bg-brand-cream-light text-brand-brown-dark truncate rounded-sm p-1.5 text-xs">
                        {item.description}
                      </div>
                    </div>
                  )}
                </>
              );

              if (!canOpenFullscreen && onMediaPress) {
                return (
                  <button
                    key={item.uuid}
                    type="button"
                    aria-label={item.description ?? defaultAlt}
                    className="relative size-full flex-shrink-0 border-0 bg-transparent p-0 text-left"
                    onClick={() => handleMediaPress(item, slide.mediaIndex)}
                  >
                    {slideContent}
                  </button>
                );
              }

              return (
                <div key={item.uuid} className="relative size-full flex-shrink-0">
                  {slideContent}
                </div>
              );
            })}
          </div>

          {canUseCarousel && slides.length > 1 && (
            <div
              className={`pointer-events-none absolute inset-0 z-20 transition-opacity duration-150 ${isCardVariant ? `opacity-0 group-hover:opacity-100 ${shouldShowArrowsOnMobile ? 'block' : 'hidden md:block'}` : 'block opacity-100'}`}
            >
              <GalleryArrowButton
                direction={'previous'}
                size={resolvedArrowSize}
                appearance={'overlay'}
                onPress={scrollPrev}
              />

              <GalleryArrowButton
                direction={'next'}
                size={resolvedArrowSize}
                appearance={'overlay'}
                onPress={scrollNext}
              />
            </div>
          )}

          <div className="pointer-events-none absolute inset-x-2 top-2 z-30 flex items-start gap-2">
            {overlay && <div className="pointer-events-auto min-w-0">{overlay}</div>}

            {canOpenFullscreen && selectedSlide && (
              <button
                type="button"
                aria-label="Open fullscreen"
                data-testid="gallery-open-fullscreen"
                onClick={() => fullscreenModalRef.current?.openSlide(selectedIndex)}
                className="focus-visible:ring-brand-green pointer-events-auto ml-auto flex size-10 shrink-0 items-center justify-center rounded border-0 bg-black/20 text-white transition-colors hover:bg-black/35 focus:outline-none focus-visible:ring-2 md:size-12"
              >
                <Icon icon={falExpand} className="size-5 text-white md:size-6" />
              </button>
            )}
          </div>

          {shouldShowCounter && (
            <div
              data-testid="gallery-counter"
              className={
                'absolute bottom-2 right-2 z-10 rounded bg-white px-2.5 py-1 text-sm font-normal text-black'
              }
            >
              {selectedIndex + 1} <span>/ {slides.length}</span>
            </div>
          )}

          {canUseCarousel && shouldShowDots && (
            <div
              aria-hidden={true}
              className={
                'pointer-events-none absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-black/35 px-1.5 py-1 backdrop-blur-sm'
              }
            >
              <GalleryDots count={slides.length} selectedIndex={selectedIndex} />
            </div>
          )}
        </div>

        {canUseCarousel && shouldShowThumbnails && slides.length > 0 && (
          <div
            className={'shrink-0 overflow-hidden md:mt-1 md:rounded-lg'}
            ref={setThumbnailsViewport}
          >
            <div className="flex">
              {slides.map((slide, i) => {
                const isSelected = selectedIndex === i;

                if (slide.type === 'ad') {
                  return (
                    <button
                      key={slide.key}
                      data-testid={`gallery-thumbnail-${i}`}
                      type="button"
                      aria-current={isSelected ? 'true' : undefined}
                      onClick={() => scrollTo(i)}
                      className={`relative w-8 shrink-0 overflow-hidden transition duration-200 md:w-10 ${aspect === 'square' ? 'h-20 md:h-32' : 'h-[60px] md:h-24'}`}
                    >
                      {isSelected && (
                        <span
                          aria-hidden={true}
                          className="border-brand-brown-dark pointer-events-none absolute inset-0 z-10 border-2"
                        />
                      )}
                      <div className="bg-brand-cream flex size-full items-center justify-center">
                        <span className="text-brand-brown-dark/50 text-2xs rotate-90 font-semibold uppercase">
                          {t('gallery.ad')}
                        </span>
                      </div>
                    </button>
                  );
                }

                const item = slide.item;

                return (
                  <button
                    key={item.uuid}
                    data-testid={`gallery-thumbnail-${i}`}
                    type="button"
                    aria-current={isSelected ? 'true' : undefined}
                    onClick={() => scrollTo(i)}
                    className={`relative w-20 shrink-0 overflow-hidden transition duration-200 md:w-32 ${thumbAspectClassName}`}
                  >
                    {isSelected && (
                      <span
                        aria-hidden={true}
                        className="border-brand-brown-dark pointer-events-none absolute inset-0 z-10 border-2"
                      />
                    )}
                    <Image
                      src={item}
                      alt={item.description ?? defaultAlt}
                      containerClassName={'size-full'}
                      imageClassName="bg-brand-cream object-cover"
                      sizeType={'card'}
                      aspectRatio={imageAspectRatio}
                      sizes="(max-width: 767px) 80px, 128px"
                    />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {canOpenFullscreen && (
          <GalleryFullscreenModal
            ref={fullscreenModalRef}
            items={items}
            defaultAlt={defaultAlt}
            aspect={aspect}
            adSlide={adSlide}
            onSelectedIndexChange={canUseCarousel ? scrollTo : undefined}
          />
        )}
      </div>
    </>
  );
}

export const Gallery = forwardRef<GalleryRef, GalleryProps>(GalleryContent);
